import { useContext, useId } from 'react'

import { TelemetryContext } from '../../context/TelemetryContext'
import { withInlineCode } from '../ProbeModal'
import { countWords, formatCount, useChatInputTelemetry } from './useChatInputTelemetry'

import './cc-chat.scss'

// The mediate arm's (CC-M) participation step, inline in the chat after the Code Coach's message:
// a prediction or decision prompt the server wrote for the student's current issue, a short
// answer box, and two ways on - "Submit response", or "I'm not sure—continue" to go on without one.
// No answer is right or wrong here, there is no minimum length and no second try, and nothing
// times out: the card waits, while the student keeps editing and running the code, until they
// answer it, skip it, start a new question (PendingLine, above the chat box) or the coding ends.
// Only ever shown for a prompt the server sent (CcChat).

export const CARD_HEADING = 'Your prediction'
export const SUBMIT_LABEL = 'Submit response'
export const SKIP_LABEL = "I'm not sure—continue"
export const START_NEW_LABEL = 'Start a new question'

// prompt: the server's text. draft / onDraftChange: the answer box, kept by CcChat. onSubmit(text),
// onSkip(): the student's choice. busy: a call to the tutor is under way (nothing can be sent
// twice). maxWords: the chat's word limit, for the answer too.
export default function ParticipationCard({ prompt, draft, onDraftChange, onSubmit, onSkip, busy = false, maxWords = null }) {
  const { logTelemetryEvent } = useContext(TelemetryContext) || {}
  const headingId = useId()
  const input = useChatInputTelemetry(draft, logTelemetryEvent, { input: 'card' })

  const wordLimit = Number(maxWords) > 0 ? Number(maxWords) : null
  const wordCount = wordLimit ? countWords(draft) : 0
  const isOverLimit = wordLimit !== null && wordCount > wordLimit
  const canSubmit = !busy && Boolean(draft.trim()) && !isOverLimit

  const submit = () => {
    if (canSubmit) onSubmit(draft)
  }

  return (
    <div className="cc-card" role="group" aria-labelledby={headingId}>
      <div className="cc-card-heading" id={headingId}>
        {CARD_HEADING}
      </div>
      {/* The prompt marks code names with backticks, as the roles' messages do. */}
      <p className="cc-card-prompt">{withInlineCode(String(prompt ?? ''))}</p>
      <textarea
        className="cc-card-input"
        aria-label="Your response"
        placeholder="A short answer, and why"
        rows={2}
        value={draft}
        disabled={busy}
        onChange={(event) => {
          input.onChange(event)
          onDraftChange(event.target.value)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            submit()
          }
          input.onKeyDown(event)
        }}
        onPaste={input.onPaste}
        onSelect={input.onSelect}
        onCopy={input.onCopy}
      />
      {isOverLimit && (
        <div className="cc-limit-note">
          {`${formatCount(wordCount)} words - the limit is ${formatCount(wordLimit)}. Shorten your answer to send it.`}
        </div>
      )}
      <div className="cc-card-actions">
        <button type="button" className="cc-button cc-button-primary" disabled={!canSubmit} onClick={submit}>
          {SUBMIT_LABEL}
        </button>
        <button type="button" className="cc-button" disabled={busy} onClick={onSkip}>
          {SKIP_LABEL}
        </button>
      </div>
    </div>
  )
}

// Above the chat box while a card waits: a message sent now answers the card, and "Start a new
// question" ends the card's round first (the server logs that as a cancellation, not an answer or
// a skip).
export function PendingLine({ busy = false, onStartNew }) {
  return (
    <div className="cc-pending-line">
      <span>Your next message will answer the prediction above.</span>
      <button type="button" className="cc-link-button" disabled={busy} onClick={onStartNew}>
        {START_NEW_LABEL}
      </button>
    </div>
  )
}
